/**
 * The audit event list (design 19 §7.2 Security tab, §7.5 drill-down, §7.6).
 *
 * One component, three uses: a gateway's security tab, a session's flow
 * timeline, and the tenant-wide Security page.
 *
 * # Why "security" is several queries
 *
 * The API's filters are AND-ed (one `event`, one `action`), which is right — it
 * keeps the LogQL the server builds provably bounded. "Everything
 * security-relevant" is therefore a UNION of the events design §5.2 marks as
 * such, fetched as separate bounded queries and merged newest-first here. The
 * alternative — letting the client hand over a selector — is the thing the
 * guard exists to prevent.
 *
 * Facets are computed over the LOADED window rather than fetched: they then
 * describe exactly the rows on screen, and clicking one cannot silently change
 * the range the counts were taken from.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import accessObservabilityService, {
  type AuditEvent,
  type AuditEventsParams,
} from '../../services/accessObservability.service';
import { resolveTimeRange, type TimeRangeValue } from '../../utils/timeRange';
import { AUDIT_EVENT_DESCRIPTIONS, SECURITY_EVENT_UNION, fmtBytes } from '../../utils/obsChart';

type FacetKey = 'reason' | 'user' | 'device' | 'app' | 'node' | 'event';

const FACETS: { key: FacetKey; label: string }[] = [
  { key: 'event', label: 'Event' },
  { key: 'reason', label: 'Reason' },
  { key: 'user', label: 'User' },
  { key: 'device', label: 'Device' },
  { key: 'app', label: 'App' },
  { key: 'node', label: 'Gateway' },
];

interface AuditEventsTableProps {
  range: TimeRangeValue;
  /**
   * 'security' unions the deny/reject/refuse events (and flow.abandon);
   * 'plain' runs one query.
   */
  preset?: 'security' | 'plain';
  gatewayId?: string;
  deviceCn?: string;
  user?: string;
  app?: string;
  sessionId?: string;
  event?: string;
  /**
   * A flow outcome, translated to the event that carries it (`flow.<x>`).
   * 'abandon' is not an audit `action` — flow.abandon carries none — but it is
   * the fourth way a flow ends, so it is offered where the other three are.
   */
  action?: 'allow' | 'deny' | 'error' | 'abandon';
  eventClass?: AuditEventsParams['eventClass'];
  search?: string;
  limit?: number;
  title?: string;
  showFacets?: boolean;
  /**
   * A short, column-trimmed list for a panel that sits under a chart.
   *
   * The Security page's counter panels used to be charts alone, so an empty one
   * could mean "the metric is missing" or "nothing happened" and the operator
   * had no way to tell. A compact list beneath each chart, with an explicit
   * empty message, makes the two distinguishable (design §9b).
   */
  compact?: boolean;
  /** What to render when the query SUCCEEDED and matched nothing. */
  emptyMessage?: string;
  testId?: string;
  /** Rendered under the header — the Security page puts its export button here. */
  actions?: React.ReactNode;
  onLoaded?: (events: AuditEvent[]) => void;
}

const EVENT_TONE: Record<string, string> = {
  'flow.deny': 'bg-danger-100 text-danger-700',
  'session.reject': 'bg-danger-100 text-danger-700',
  'posture.reject': 'bg-danger-100 text-danger-700',
  'policy.rejected': 'bg-danger-100 text-danger-700',
  'l3.spoof_drop': 'bg-danger-100 text-danger-700',
  'connector.refuse': 'bg-warning-100 text-warning-700',
  // Not a refusal (nothing was decided), but not routine either: an attempt
  // whose handshake never completed (VSNP-1427).
  'flow.abandon': 'bg-warning-100 text-warning-700',
  'flow.error': 'bg-warning-100 text-warning-700',
  'flow.allow': 'bg-success-100 text-success-700',
  'flow.close': 'bg-gray-100 text-gray-600',
};

export const AuditEventsTable = ({
  range,
  preset = 'plain',
  gatewayId,
  deviceCn,
  user,
  app,
  sessionId,
  event,
  action,
  eventClass,
  search,
  limit = 300,
  title = 'Events',
  showFacets = false,
  compact = false,
  emptyMessage = 'No events in this window.',
  testId = 'audit-events',
  actions,
  onLoaded,
}: AuditEventsTableProps) => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [queries, setQueries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [facet, setFacet] = useState<{ key: FacetKey; value: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { fromIso, toIso } = resolveTimeRange(range);
      const base: AuditEventsParams = {
        gatewayId, deviceCn, user, app, sessionId, eventClass, search,
        from: fromIso, to: toIso, limit,
      };
      // WP4 dev-1 finding: the gateway's audit lines carry NO `action` field —
      // design §4.2 lists `action` as structured metadata, but the producer
      // emits the verdict only in the event name (`flow.allow` / `flow.deny` /
      // `flow.error`). Filtering by `action` therefore matches nothing, and
      // silently: an empty list looks exactly like "no denies happened". So an
      // action filter is translated into the event that carries it, and the
      // raw `action` is passed through only when no event is implied.
      const actionEvent = action ? `flow.${action}` : undefined;
      const requests: AuditEventsParams[] =
        preset === 'security'
          ? SECURITY_EVENT_UNION.map((e) => ({ ...base, event: e }))
          : [{ ...base, event: event ?? actionEvent }];

      const results = await Promise.all(
        requests.map((p) =>
          accessObservabilityService
            .listAuditEvents(p, { showLoading: false })
            .catch(() => ({ from: '', to: '', count: 0, query: '', events: [] })),
        ),
      );
      const merged = results.flatMap((r) => r.events);
      merged.sort((a, b) => (a.ts < b.ts ? 1 : a.ts > b.ts ? -1 : 0));
      setEvents(merged);
      setQueries(results.map((r) => r.query).filter(Boolean));
      onLoaded?.(merged);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
    // onLoaded is intentionally excluded: a parent's inline callback would
    // restart this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, preset, gatewayId, deviceCn, user, app, sessionId, event, action, eventClass, search, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  const facetValues = useMemo(() => {
    const out: Record<FacetKey, Record<string, number>> = {
      reason: {}, user: {}, device: {}, app: {}, node: {}, event: {},
    };
    // The security view names every event it asked for, zero or not. A facet
    // that listed only what happened to load would hide the question "were
    // there any abandoned flows?" behind "none of them appeared" — and would
    // leave no way to see which events the view covers at all.
    if (preset === 'security') {
      for (const name of SECURITY_EVENT_UNION) out.event[name] = 0;
    }
    for (const e of events) {
      for (const f of FACETS) {
        const v = (e as unknown as Record<string, string | undefined>)[f.key];
        if (!v) continue;
        out[f.key][v] = (out[f.key][v] ?? 0) + 1;
      }
    }
    return out;
  }, [events, preset]);

  const shown = useMemo(() => {
    if (!facet) return events;
    return events.filter((e) => (e as unknown as Record<string, string | undefined>)[facet.key] === facet.value);
  }, [events, facet]);

  return (
    <div className="card" data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="mr-auto text-sm font-semibold text-gray-900">
          {title}{' '}
          <span className="ml-1 text-xs font-normal text-gray-400" data-testid={`${testId}-count`}>
            {shown.length}
            {facet ? ` of ${events.length}` : ''} event{shown.length === 1 ? '' : 's'}
          </span>
        </h3>
        {actions}
        <button type="button" className="btn-secondary px-2 py-1" onClick={() => void load()} title="Refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {showFacets && (
        <div className="mb-3 space-y-1">
          {FACETS.map((f) => {
            const values = Object.entries(facetValues[f.key]).sort((a, b) => b[1] - a[1]).slice(0, 8);
            if (values.length === 0) return null;
            return (
              <div key={f.key} className="flex flex-wrap items-center gap-1 text-xs">
                <span className="w-16 shrink-0 text-gray-400">{f.label}</span>
                {values.map(([value, count]) => {
                  const on = facet?.key === f.key && facet.value === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFacet(on ? null : { key: f.key, value })}
                      data-testid={`facet-${f.key}-${value}`}
                      title={f.key === 'event' ? AUDIT_EVENT_DESCRIPTIONS[value] : undefined}
                      className={`rounded-full border px-2 py-0.5 transition-colors ${
                        on ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300'
                      }`}
                    >
                      {value} <span className={on ? 'text-white/70' : 'text-gray-400'}>{count}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {error && <div className="mb-2 rounded border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700">{error}</div>}

      <div className={compact ? 'max-h-56 overflow-auto' : 'max-h-[32rem] overflow-auto'}>
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Time</th>
              <th className="py-2 pr-3">Event</th>
              {!compact && <th className="py-2 pr-3">User</th>}
              <th className="py-2 pr-3">Device</th>
              <th className="py-2 pr-3">{compact ? 'Host / dest' : 'App / host / dest'}</th>
              {!compact && <th className="py-2 pr-3">Rule</th>}
              <th className="py-2 pr-3">Reason</th>
              {!compact && <th className="py-2 pr-3 text-right">Bytes</th>}
              {!compact && <th className="py-2 pr-3">Gateway</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={9} className="py-6 text-center text-gray-400">Loading…</td></tr>
            ) : error ? null : shown.length === 0 ? (
              // An explicit statement, not a blank box: "the query ran and
              // found nothing in this range" is a different fact from "the
              // query failed", and the error banner above covers the second.
              <tr><td colSpan={9} className="py-6 text-center text-gray-400" data-testid={`${testId}-empty`}>{emptyMessage}</td></tr>
            ) : (
              shown.map((e, i) => (
                <tr key={`${e.node}-${e.seq ?? i}-${e.ts}`} className="hover:bg-gray-50" data-testid="audit-row" data-event={e.event}>
                  <td className="whitespace-nowrap py-1.5 pr-3 font-mono text-[11px] text-gray-500">
                    {e.ts.replace('T', ' ').slice(0, 23)}
                  </td>
                  <td className="py-1.5 pr-3">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${EVENT_TONE[e.event ?? ''] ?? 'bg-gray-100 text-gray-600'}`}
                      title={AUDIT_EVENT_DESCRIPTIONS[e.event ?? '']}
                    >
                      {e.event ?? '—'}
                    </span>
                  </td>
                  {/* The audit record's `user` is the policy subject. Flow
                      events carry no `idp_verified` (only session.open does),
                      so the test is "the subject is not just the certificate
                      CN" — which is exactly what an IdP-verified subject looks
                      like, and what the identity rule (brief §3 WP4) is for. */}
                  {!compact && (
                    <td className="py-1.5 pr-3 text-xs">
                      {e.raw?.idp_verified !== false && e.user && e.user !== e.device ? (
                        <span className="text-gray-900">{e.user}</span>
                      ) : (
                        <span className="italic text-gray-400" title="no IdP identity — matched by device certificate">device-only</span>
                      )}
                    </td>
                  )}
                  <td className="py-1.5 pr-3 text-xs text-gray-700">{e.device ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-xs">
                    {/* The HOST leads, because "what site was this" is the
                        question the audit stream is most often asked and the
                        app name answers it only for registered apps — an
                        `$internet` flow's whole identity is its hostname
                        (design §9b). */}
                    {e.host && <span className="font-medium text-gray-900" data-testid="audit-host">{e.host}</span>}
                    {!compact && <span className={e.host ? 'ml-1 text-gray-500' : 'text-gray-800'}>{e.app ?? '—'}</span>}
                    {e.dest && <span className="ml-1 font-mono text-[10px] text-gray-400">{e.dest}</span>}
                    {e.layer && !compact && <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] text-gray-500">{e.layer}</span>}
                  </td>
                  {!compact && <td className="py-1.5 pr-3 font-mono text-[11px] text-gray-500">{e.rule ?? '—'}</td>}
                  <td className="py-1.5 pr-3 text-xs text-gray-700" title={e.detail ?? ''}>{e.reason ?? '—'}</td>
                  {/* Bytes ride on the CLOSE events (flow.close / session.close),
                      never on the admission ones: at flow.allow nothing has been
                      transferred yet, so a byte count there would be a zero that
                      reads as "no traffic". */}
                  {!compact && (
                    <td className="whitespace-nowrap py-1.5 pr-3 text-right text-[11px] text-gray-600">
                      {typeof e.raw?.bytes_c2s === 'number' || typeof e.raw?.bytes_s2c === 'number' ? (
                        <span title="client→server / server→client">
                          ↑{fmtBytes(Number(e.raw?.bytes_c2s ?? 0))} ↓{fmtBytes(Number(e.raw?.bytes_s2c ?? 0))}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  )}
                  {!compact && <td className="py-1.5 pr-3 font-mono text-[11px] text-gray-500">{e.node}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {queries.length > 0 && !compact && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-gray-400">Queries the guard built ({queries.length})</summary>
          <ul className="mt-1 space-y-0.5" data-testid={`${testId}-queries`}>
            {queries.map((q) => (
              <li key={q} className="break-all font-mono text-[10px] text-gray-400">{q}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default AuditEventsTable;
